import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiService } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import UniversalModal from '../Common/UniversalModal';
import Toast from '../Common/Toast';
import '../Common/CardActions.css';
import Pagination from '../Common/Pagination';

const emptyForm = {
  name: '',
  court_type: 'district',
};

const COURT_TYPE_LABELS = {
  district: 'Арбитражные суды округов',
  appeal: 'Арбитражные апелляционные суды',
  region: 'Арбитражные суды субъектов РФ',
};

const isAdminUser = (user) => {
  return !!user?.is_staff || !!user?.is_superuser || String(user?.role || '').toLowerCase() === 'admin';
};

const formatImportStatus = (s) => {
  if (s === 'running') return 'Выполняется';
  if (s === 'pending') return 'В очереди';
  if (s === 'done') return 'Завершено';
  if (s === 'failed') return 'Ошибка';
  return s || '—';
};

const CourtsList = () => {
  const { user } = useAuth();
  const isAdmin = isAdminUser(user);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(24);

  const [modalState, setModalState] = useState({ isOpen: false, mode: null, item: null }); // create|view|edit
  const [formData, setFormData] = useState(emptyForm);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const [importJob, setImportJob] = useState(null);
  const importJobRef = useRef(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importStarting, setImportStarting] = useState(false);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 2500);
  };

  const load = useCallback(async () => {
    if (!isAdmin) return;
    try {
      setLoading(true);
      setError('');
      const all = [];
      let page = 1;
      while (true) {
        const resp = await apiService.getCourts(`?page=${page}`);
        const chunk = resp.data.results || [];
        all.push(...chunk);
        if (!resp.data.next) break;
        page += 1;
        if (page > 200) break;
      }
      setItems(all);
    } catch (err) {
      setError('Ошибка при загрузке судов: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    (async () => {
      try {
        const resp = await apiService.getUiSettings();
        const v = Number(resp?.data?.table_page_size);
        if ([12, 24, 48, 96].includes(v)) {
          setItemsPerPage(v);
        }
      } catch (_) {
        // ignore
      }
    })();
  }, []);

  const loadLatestImportJob = useCallback(async () => {
    if (!isAdmin) return;
    setImportLoading(true);
    try {
      const resp = await apiService.listCourtImportJobs('?page=1');
      const first = (resp.data?.results || [])[0] || null;
      setImportJob(first);
    } catch (err) {
      console.error('Не удалось загрузить статус импорта судов', err);
    } finally {
      setImportLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);
  useEffect(() => { if (isAdmin) loadLatestImportJob(); }, [isAdmin, loadLatestImportJob]);

  useEffect(() => { importJobRef.current = importJob; }, [importJob]);

  useEffect(() => {
    if (!isAdmin) return undefined;
    const timer = setInterval(async () => {
      const job = importJobRef.current;
      if (!job?.id) return;
      if (job.status !== 'pending' && job.status !== 'running') return;
      try {
        const resp = await apiService.getCourtImportJob(job.id);
        setImportJob(resp.data);
      } catch (_) {
        // ignore
      }
    }, 1200);
    return () => clearInterval(timer);
  }, [isAdmin]);

  const startImport = async () => {
    if (!isAdmin) return;
    setImportStarting(true);
    try {
      const resp = await apiService.startCourtImport();
      setImportJob(resp.data);
      showToast('Импорт судов запущен');
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      showToast('Не удалось запустить импорт судов: ' + msg, 'error');
    } finally {
      setImportStarting(false);
    }
  };

  const refreshImportStatus = async () => {
    if (!isAdmin) return;
    if (!importJob?.id) return;
    try {
      const resp = await apiService.getCourtImportJob(importJob.id);
      setImportJob(resp.data);
    } catch (_) {
      // ignore
    }
  };

  const filtered = useMemo(() => {
    const q = String(search || '').trim().toLowerCase();
    if (!q) return items;
    return (items || []).filter((s) => {
      const hay = `${s.name || ''} ${COURT_TYPE_LABELS[s.court_type] || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [items, search]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, itemsPerPage, items]);

  const totalItems = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const paginated = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filtered.slice(start, start + itemsPerPage);
  }, [filtered, currentPage, itemsPerPage]);

  const handleItemsPerPageChange = (v) => {
    setItemsPerPage(v);
    setCurrentPage(1);
    apiService.saveUiSettings({ table_page_size: v }).catch(() => {});
  };

  const openCreate = () => {
    setFormData(emptyForm);
    setHasUnsavedChanges(false);
    setModalState({ isOpen: true, mode: 'create', item: null });
  };

  const openView = (item) => {
    setFormData({
      name: item?.name || '',
      court_type: item?.court_type || 'district',
    });
    setHasUnsavedChanges(false);
    setModalState({ isOpen: true, mode: 'view', item });
  };

  const openEdit = (item) => {
    setFormData({
      name: item?.name || '',
      court_type: item?.court_type || 'district',
    });
    setHasUnsavedChanges(false);
    setModalState({ isOpen: true, mode: 'edit', item });
  };

  const closeModal = () => {
    setModalState({ isOpen: false, mode: null, item: null });
    setFormData(emptyForm);
    setHasUnsavedChanges(false);
  };

  const onField = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setHasUnsavedChanges(true);
  };

  const handleSave = async ({ exit } = {}) => {
    if (!isAdmin) return;
    try {
      let saved = null;
      if (modalState.item?.id) {
        const resp = await apiService.updateCourt(modalState.item.id, formData);
        saved = resp.data;
        setItems((prev) => (prev || []).map((x) => (x.id === saved.id ? saved : x)));
        showToast('Изменения сохранены');
      } else {
        const resp = await apiService.createCourt(formData);
        saved = resp.data;
        setItems((prev) => [saved, ...(prev || [])]);
        showToast('Суд добавлен');
      }
      setHasUnsavedChanges(false);
      if (exit) {
        closeModal();
      } else {
        openEdit(saved);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      showToast('Ошибка при сохранении суда: ' + msg, 'error');
    }
  };

  const handleDelete = async (item) => {
    if (!isAdmin) return;
    if (!window.confirm(`Удалить суд ${item.name}?`)) return;
    try {
      await apiService.deleteCourt(item.id);
      setItems((prev) => (prev || []).filter((x) => x.id !== item.id));
      showToast('Суд удален');
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      showToast('Ошибка при удалении суда: ' + msg, 'error');
    }
  };

  if (!isAdmin) {
    return (
      <div style={{ padding: 20 }}>
        <h1>⚖️ Суды</h1>
        <div style={{ padding: '16px 0', color: '#6c757d' }}>Раздел доступен только администраторам.</div>
      </div>
    );
  }

  if (loading) {
    return <div style={{ padding: 50, textAlign: 'center' }}><h2>Загрузка судов...</h2></div>;
  }

  const progressPct = Math.max(
    0,
    Math.min(100, (importJob?.total_pages ? (importJob.current_page / importJob.total_pages) * 100 : 0)),
  );

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1>⚖️ Суды</h1>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button
            type="button"
            onClick={load}
            style={{ padding: '10px 16px', background: '#f8f9fa', color: '#2c3e50', border: '1px solid #dee2e6', borderRadius: 8, cursor: 'pointer' }}
          >
            Обновить
          </button>
          <button
            type="button"
            onClick={startImport}
            disabled={importStarting || importJob?.status === 'running' || importJob?.status === 'pending'}
            style={{ padding: '10px 16px', background: '#0d6efd', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' }}
            title="Импорт с arbitr.ru"
          >
            {importStarting ? 'Запуск…' : 'Импорт с arbitr.ru'}
          </button>
          <button
            type="button"
            onClick={openCreate}
            style={{ padding: '10px 16px', background: '#007bff', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' }}
          >
            Добавить
          </button>
        </div>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e9ecef', borderRadius: 12, padding: 14, marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ color: '#6c757d' }}>
            Статус импорта: <b>{formatImportStatus(importJob?.status)}</b>
            {importLoading && <span> · загрузка…</span>}
          </div>
          <button type="button" onClick={refreshImportStatus} disabled={!importJob?.id} style={{ padding: '8px 12px', border: '1px solid #dee2e6', borderRadius: 8, background: '#f8f9fa' }}>
            Обновить статус
          </button>
          <div style={{ color: '#6c757d' }}>
            {importJob
              ? `стр. ${importJob.current_page || 0}/${importJob.total_pages || 0} · обработано ${importJob.processed || 0} · создано ${importJob.created || 0} · обновлено ${importJob.updated || 0} · пропущено ${importJob.skipped || 0}`
              : '—'}
          </div>
        </div>
        <div style={{ marginTop: 10, height: 10, background: '#e9ecef', borderRadius: 999, overflow: 'hidden' }}>
          <div style={{ width: `${progressPct}%`, height: '100%', background: importJob?.status === 'failed' ? '#dc3545' : '#0d6efd', transition: 'width 0.2s ease' }} />
        </div>
        {!!importJob?.message && (
          <div style={{ marginTop: 10, color: '#495057', whiteSpace: 'pre-wrap' }}>
            {String(importJob.message).trim()}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Поиск по названию..."
          style={{ flex: 1, minWidth: 260, padding: 10, border: '1px solid #dee2e6', borderRadius: 10 }}
        />
        <div style={{ color: '#6c757d', whiteSpace: 'nowrap' }}>
          Найдено: <b>{filtered.length}</b>
        </div>
      </div>

      {error && (
        <div style={{ background: '#fee', border: '1px solid #fcc', color: '#a00', padding: 12, borderRadius: 8, marginBottom: 16 }}>
          {error}
        </div>
      )}

      <div style={{ background: '#fff', border: '1px solid #e9ecef', borderRadius: 12, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
          <thead>
            <tr style={{ background: '#f8f9fa' }}>
              <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e9ecef' }}>Наименование</th>
              <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e9ecef', width: 320 }}>Тип</th>
              <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e9ecef', width: 140 }}>Действия</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={3} style={{ padding: 20, textAlign: 'center', color: '#6c757d' }}>
                  Суды не найдены
                </td>
              </tr>
            ) : paginated.map((item) => (
              <tr key={item.id} onClick={() => openView(item)} style={{ cursor: 'pointer' }}>
                <td style={{ padding: '10px 12px', borderBottom: '1px solid #f1f3f5' }}>{item.name || '—'}</td>
                <td style={{ padding: '10px 12px', borderBottom: '1px solid #f1f3f5' }}>{COURT_TYPE_LABELS[item.court_type] || item.court_type}</td>
                <td style={{ padding: '10px 12px', borderBottom: '1px solid #f1f3f5' }}>
                  <div className="card-actions" style={{ justifyContent: 'flex-start' }}>
                    <button
                      type="button"
                      className="card-action-btn card-action-btn--edit"
                      title="Редактировать"
                      onClick={(e) => { e.stopPropagation(); openEdit(item); }}
                    >
                      ✎
                    </button>
                    <button
                      type="button"
                      className="card-action-btn card-action-btn--delete"
                      title="Удалить"
                      onClick={(e) => { e.stopPropagation(); handleDelete(item); }}
                    >
                      🗑️
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        itemsPerPage={itemsPerPage}
        totalItems={totalItems}
        onItemsPerPageChange={handleItemsPerPageChange}
      />

      <UniversalModal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        title={
          modalState.mode === 'create'
            ? 'Добавление суда'
            : modalState.mode === 'edit'
              ? 'Редактирование суда'
              : 'Суд'
        }
        onSave={() => handleSave({ exit: false })}
        onSaveAndExit={() => handleSave({ exit: true })}
        onCancel={closeModal}
        isEditing={modalState.mode !== 'view'}
        hasUnsavedChanges={hasUnsavedChanges}
      >
        <div className="form-group">
          <label>Наименование</label>
          <input
            type="text"
            className="form-control"
            value={formData.name}
            onChange={(e) => onField('name', e.target.value)}
            disabled={modalState.mode === 'view'}
          />
        </div>
        <div className="form-group">
          <label>Тип суда</label>
          <select
            className="form-control"
            value={formData.court_type}
            onChange={(e) => onField('court_type', e.target.value)}
            disabled={modalState.mode === 'view'}
          >
            <option value="district">Арбитражные суды округов</option>
            <option value="appeal">Арбитражные апелляционные суды</option>
            <option value="region">Арбитражные суды субъектов РФ</option>
          </select>
        </div>
      </UniversalModal>

      <Toast toast={toast} />
    </div>
  );
};

export default CourtsList;
